import {
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import { wolfswapCookieApi } from '@/lib/api/clients/wolfswap';
import type { SelfSignedTradingSessionProof } from '@/lib/api/tradingSession/types';

import {
    associationSignatureSchema,
    authorizationRegistryChallengeSchema,
    authorizationRegistryProvisionSchema,
} from './schemas';
import type {
    AssociationSignature,
    AuthorizationRegistryChallenge,
    AuthorizationRegistryProvision,
    OwnerBindingProof,
} from './schemas';

/**
 * The association signature is minted for a wallet on ONE chain, so `networkId`
 * is the anchor the authorization wallet lives on.
 */
export async function requestAccountHashAssociation(
    authorizationWallet: string,
    accountHash: string,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Promise<AssociationSignature> {
    const data = await wolfswapCookieApi
        .post('v2/custodial/association-signature', {
            json: {
                networkId: anchorChainId,
                intent: 'link',
                subject: authorizationWallet,
                accountHashes: [accountHash],
            },
        })
        .json();
    return associationSignatureSchema.parse(data);
}

export async function requestAuthorizationRegistryChallenge(
    owner: string,
    accountHash: string,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Promise<AuthorizationRegistryChallenge> {
    const data = await wolfswapCookieApi
        .post('v2/auth-registry/challenge', {
            json: { owner, accountHash, anchorChainId },
        })
        .json();
    return assertAnchor(
        authorizationRegistryChallengeSchema.parse(data),
        anchorChainId,
    );
}

export async function provisionAuthorizationWallet(input: {
    owner: string;
    accountHash: string;
    ownerBinding: OwnerBindingProof;
    anchorChainId?: AnchorChainId;
}): Promise<AuthorizationRegistryProvision> {
    const anchorChainId = input.anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID;
    const data = await wolfswapCookieApi
        .post('v2/auth-registry/provision', {
            json: { ...input, anchorChainId },
        })
        .json();
    return assertAnchor(
        authorizationRegistryProvisionSchema.parse(data),
        anchorChainId,
    );
}

/**
 * A response that names a DIFFERENT anchor than the one requested would mean the
 * wallet is being provisioned somewhere the user did not choose, so it stops
 * here. An older backend names none, which can only ever have meant Cronos.
 */
function assertAnchor<T extends { anchorChainId?: AnchorChainId }>(
    response: T,
    expected: AnchorChainId,
): T {
    if (
        response.anchorChainId !== undefined &&
        response.anchorChainId !== expected
    ) {
        throw new Error(
            'The server answered for a different network than the one ' +
                'requested; nothing was signed.',
        );
    }
    return response;
}

/**
 * EOA-only (wallet-first) mode: omitting `accountHash` makes the server derive
 * it as `eoaBootstrapHash(owner)` and commit the binding to that single-element
 * hash set.
 */
export async function requestEoaAuthorizationChallenge(
    owner: string,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Promise<AuthorizationRegistryChallenge> {
    const data = await wolfswapCookieApi
        .post('v2/auth-registry/challenge', {
            json: { owner, anchorChainId },
        })
        .json();
    return assertAnchor(
        authorizationRegistryChallengeSchema.parse(data),
        anchorChainId,
    );
}

/**
 * Wallet-only provisioning: there is no OAuth identity to authorize the create,
 * so the owner's self-signed trading-session statement is the create authority
 * RustSigner accepts instead.
 */
export async function provisionEoaAuthorizationWallet(input: {
    owner: string;
    ownerBinding: OwnerBindingProof;
    tradingSessionProof: SelfSignedTradingSessionProof;
    anchorChainId?: AnchorChainId;
}): Promise<AuthorizationRegistryProvision> {
    const anchorChainId = input.anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID;
    const data = await wolfswapCookieApi
        .post('v2/auth-registry/provision', {
            json: { ...input, anchorChainId },
        })
        .json();
    return assertAnchor(
        authorizationRegistryProvisionSchema.parse(data),
        anchorChainId,
    );
}
